import type { ZplPrinterListItem } from "./zplPrinterDisplay";

export type ZplLabelRequirement = {
  labelSize: string;
  /** When 0, any DPI at that station and stock matches. */
  dpi: number;
};

export type PublicStationOption = {
  id: number;
  name: string;
  siteCode: string | null;
};

const LEGACY_STATION_NAME: Record<string, string> = {
  Inv: "Inventory",
  Rec: "Receiving",
  Ship: "Shipping",
};

export function zplPrintStationLabel(stationId: string, stations: readonly PublicStationOption[]): string {
  const id = Number(stationId);
  const station = stations.find((row) => row.id === id);
  if (station == null) return "this location";
  return station.name;
}

export function isZplPrintStationId(value: string | null | undefined): boolean {
  const trimmed = value?.trim() ?? "";
  return /^[0-9]+$/.test(trimmed);
}

export type ZplPrinterResolution = {
  printerName: string;
  unavailableMessage: string | null;
};

export type ZplPrintDocument = {
  id: string;
  /** Short name shown in the location list, for example "Carton". */
  label: string;
  labelSize: string;
  /** When 0, any dpi at that stock matches. */
  dpi: number;
};

export function printerIsOnlineAndEnabled(printer: ZplPrinterListItem): boolean {
  if (printer.online !== true) return false;
  return (printer.statusError ?? "").trim().toLowerCase() !== "disabled";
}

/** Online stock at a station, for example "4x2-300, 4x6-300". Null when none are online. */
export function formatStationLabelStockDetail(
  stationId: number,
  printers: readonly ZplPrinterListItem[],
): string | null {
  const parts = printers
    .filter((printer) => printer.stationId === stationId && printerIsOnlineAndEnabled(printer))
    .map((printer) => {
      const size = printer.labelSize?.trim() || "?";
      const dpi = printer.dpi != null && printer.dpi > 0 ? String(printer.dpi) : "?";
      return `${size}-${dpi}`;
    });
  const unique = [...new Set(parts)].sort();
  if (unique.length === 0) return null;
  return unique.join(", ");
}

function printerCoversDocument(printer: ZplPrinterListItem, document: ZplPrintDocument): boolean {
  if (!stockMatches(printer, document.labelSize)) return false;
  if (document.dpi === 0) return true;
  return printer.dpi === document.dpi;
}

/**
 * Which of this page's documents an online printer at the station can print.
 * "All" when every document is covered. Otherwise the covered names, separated by "; ".
 * "None" when the station has an online printer that does not cover these documents.
 * Null when the station has no online, enabled printer.
 */
export function formatStationDocumentCoverage(
  stationId: number,
  printers: readonly ZplPrinterListItem[],
  documents: readonly ZplPrintDocument[],
): string | null {
  if (documents.length === 0) return formatStationLabelStockDetail(stationId, printers);
  const online = printers.filter(
    (printer) => printer.stationId === stationId && printerIsOnlineAndEnabled(printer),
  );
  if (online.length === 0) return null;
  const covered = documents.filter((document) =>
    online.some((printer) => printerCoversDocument(printer, document)),
  );
  if (covered.length === 0) return "None";
  if (covered.length === documents.length) return "All";
  return covered.map((document) => document.label).join("; ");
}

function stockMatches(printer: ZplPrinterListItem, labelSize: string): boolean {
  const wanted = labelSize.trim().toLowerCase();
  const actual = printer.labelSize?.trim().toLowerCase() ?? "";
  return wanted === "" || (wanted !== "" && actual === wanted);
}

export function resolveZplPrinterAtStation(
  printers: readonly ZplPrinterListItem[],
  stationId: string,
  requirement: ZplLabelRequirement,
): ZplPrinterResolution {
  const id = Number(stationId);
  if (!Number.isInteger(id)) {
    return { printerName: "", unavailableMessage: "Pick a location." };
  }
  const labelSize = requirement.labelSize.trim();
  const dpi = requirement.dpi;
  let matches = printers.filter(
    (printer) =>
      printer.stationId === id &&
      stockMatches(printer, labelSize) &&
      (dpi === 0 || printer.dpi === dpi),
  );
  if (matches.length === 0 && dpi !== 0 && dpi !== 203) {
    matches = printers.filter(
      (printer) =>
        printer.stationId === id && stockMatches(printer, labelSize) && printer.dpi === 203,
    );
  }
  if (matches.length === 0) {
    return {
      printerName: "",
      unavailableMessage: labelSize === "" ? "No printer at that location." : `No ${labelSize} printer at that location.`,
    };
  }
  const online = matches.find((printer) => printer.online !== false);
  const chosen = online ?? matches[0]!;
  if (chosen.online === false) {
    const name = chosen.displayName?.trim() || chosen.identifier?.trim() || chosen.name;
    return { printerName: "", unavailableMessage: `${name} is offline.` };
  }
  return { printerName: chosen.name, unavailableMessage: null };
}

export function stationIdFromStoredPrinter(
  stored: string,
  stations: readonly PublicStationOption[],
  printers: readonly ZplPrinterListItem[] | undefined,
): string {
  const trimmed = stored.trim();
  if (isZplPrintStationId(trimmed) && stations.some((station) => String(station.id) === trimmed)) {
    return trimmed;
  }
  const legacyName = LEGACY_STATION_NAME[trimmed];
  if (legacyName != null) {
    const station = stations.find((row) => row.name === legacyName && (row.siteCode == null || row.siteCode === "sgs"));
    if (station != null) return String(station.id);
  }
  const match = printers?.find((printer) => printer.name === trimmed || printer.identifier === trimmed);
  if (match?.stationId != null) return String(match.stationId);
  return "";
}
